import { getMemoFirestoreInstance } from '@/lib/memoFirebase'

export interface LinkedMemo {
  syncId: string
  title: string
  body: string
  tags: string[]
  color: string
  isStarred: boolean
  isPinned: boolean
  folderId: string | null
  deletedAt?: string | null
  createdAt: string
  updatedAt: string
}

/** Memo Firestore에서 메모 목록 조회 (읽기 전용) */
export async function fetchMemos(memoUid: string): Promise<LinkedMemo[]> {
  const firestore = await getMemoFirestoreInstance()
  if (!firestore) return []

  const { collection, getDocs, query, orderBy } = await import('firebase/firestore')

  // Memo 앱은 soft-delete (deletedAt 필드) — 삭제된 메모 제외
  const memosRef = collection(firestore, 'users', memoUid, 'memos')
  const q = query(memosRef, orderBy('updatedAt', 'desc'))
  const snapshot = await getDocs(q)

  return snapshot.docs
    .map(doc => ({ syncId: doc.id, ...doc.data() }) as LinkedMemo)
    .filter(m => !m.deletedAt) // soft-delete 필터
}

/** 특정 메모 1건 조회 */
export async function fetchMemo(memoUid: string, syncId: string): Promise<LinkedMemo | null> {
  const firestore = await getMemoFirestoreInstance()
  if (!firestore) return null

  const { doc, getDoc } = await import('firebase/firestore')

  const memoDoc = await getDoc(doc(firestore, 'users', memoUid, 'memos', syncId))
  if (!memoDoc.exists()) return null

  return { syncId: memoDoc.id, ...memoDoc.data() } as LinkedMemo
}

/** 메모 검색 (제목/태그/본문 기반, 캐시 또는 fetch) */
export function searchMemosFromCache(memos: LinkedMemo[], keyword: string): LinkedMemo[] {
  const lower = keyword.toLowerCase()
  return memos.filter(m =>
    m.title?.toLowerCase().includes(lower) ||
    m.tags?.some(t => t.toLowerCase().includes(lower)) ||
    m.body?.toLowerCase().includes(lower)
  )
}

/** 메모 검색 (fetch 후 필터링, 캐시 없을 때 사용) */
export async function searchMemos(memoUid: string, keyword: string): Promise<LinkedMemo[]> {
  const memos = await fetchMemos(memoUid)
  return searchMemosFromCache(memos, keyword)
}
